import { type WebConfig } from '@koala/sdk';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useEffect, Fragment } from 'react';
import { type ConnectedProps, connect } from 'react-redux';
import { compose } from 'redux';
import styled, { withTheme } from 'styled-components';
import { ConnectedStarIcon } from '../uielements';
import { StoredAddressOptionsMenu } from './menu';
import {
  StyledAddressContainer,
  StyledAddressOptionsContainer,
  StyledSpecialInstructions,
} from './styles';
import {
  StyledCheckoutComponent,
  StyledHeaderText,
  StyledStepHeader,
} from '@/components/checkout/uielements';
import StringAccessor from '@/components/cmsConfig/stringAccessor';
import Icon from '@/components/uielements/icon';
import meActions from '@/redux/me/actions';
import { type RootState } from '@/types/app';

interface ThemeProps {
  theme: WebConfig;
}

/** @TODO remove `withTheme` HoC. */
type Props = ThemeProps & ReduxProps;

const _StoredAddresses = ({
  myAddresses,
  fetchAddresses,
  toggleStoredAddressesMenu,
  setActiveStoredAddress,
  theme,
}: Props) => {
  // Don't show section if feature disabled
  if (!theme.accounts?.stored_addresses) {
    return null;
  }

  useEffect(() => {
    fetchAddresses();

    return () => {
      toggleStoredAddressesMenu(false);
      /** @TODO allow setting `null` addresses. */
      // @ts-expect-error
      setActiveStoredAddress(null);
    };
  }, []);

  // Don't show section if no stored addresses
  if (myAddresses?.length < 1) {
    return null;
  }

  return (
    <StyledCheckoutComponent>
      <StyledStepHeader $enabled={true}>
        <StringAccessor tag={StyledHeaderText} accessor="account.addresses_header" html={true} />
      </StyledStepHeader>
      {myAddresses.map((address) => (
        <Fragment key={address.id}>
          <StyledAddressContainer>
            <p>
              {/* If default address */}
              {address.default && <ConnectedStarIcon />}
              {/* Address details */}
              {address.street_address}
              <br />
              {address.street_address_2 && (
                <>
                  {address.street_address_2}
                  <br />
                </>
              )}
              <span title={`${address.id}`}>{address.city}</span>, {address.state}{' '}
              {address?.zip_code}
            </p>

            {/* Special instructions, if they exist */}
            {address.special_instructions && (
              <StringAccessor
                tag={StyledSpecialInstructions}
                accessor="account.addresses_special_instructions_text"
                html={true}
                dataObj={{ specialInstructions: address.special_instructions }}
              />
            )}

            {/* Address Options menu */}
            <StyledAddressOptionsContainer>
              <DropdownMenu.Root>
                <Edit>
                  <Icon.Ellipsis title="Edit favorite location" />
                </Edit>

                <DropdownMenu.Content>
                  <StoredAddressOptionsMenu address={address} />
                </DropdownMenu.Content>
              </DropdownMenu.Root>
            </StyledAddressOptionsContainer>
          </StyledAddressContainer>
        </Fragment>
      ))}
    </StyledCheckoutComponent>
  );
};

const mapStateToProps = (state: RootState) => ({
  myAddresses: state.app.me.myAddresses,
});

const mapDispatchToProps = {
  fetchAddresses: meActions.fetchAddresses,
  toggleStoredAddressesMenu: meActions.toggleStoredAddressesMenu,
  setActiveStoredAddress: meActions.setActiveStoredAddress,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;
export const StoredAddresses = compose<any>(withTheme, connector)(_StoredAddresses);

const Edit = styled(DropdownMenu.Trigger)`
  appearance: none;
  background: none;
  border: none;
  cursor: pointer;
`;
